import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Dropzone } from '@/components/ui/dropzone'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  CategoryTag,
  DocumentTypeTag,
  type Project,
  type Folder,
} from '@/data/entities'
import { Label } from '@/components/ui/label'
import { GenerateSOPButton } from './GenerateSOPButton'
import { TypeTagSelector, CategoryTagSelector } from '@/components/TagSelector'
import { useRouteContext } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { getFolders, queryKeys } from '@/data/queries'
import NewFolderDialog from './NewFolderDialog'
import { FolderPlusIcon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useUploadingStore } from '@/stores/useUploadingStore'
import { useUserSettingsStore } from '@/stores/useUserSettingsStore'
import { Separator } from '@/components/ui/separator'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@radix-ui/react-collapsible'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { ChevronDownIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export function GenerateDocumentDialog({
  children,
  currentProjectId,
  projects,
  isMobileVersion = false,
  isOpen = false,
  defaultCategory,
  canCreateWorkspace = true,
}: {
  children: React.ReactNode
  currentProjectId: string | null
  projects: Project[]
  isMobileVersion?: boolean
  isOpen?: boolean
  defaultCategory?: CategoryTag
  canCreateWorkspace?: boolean
}) {
  const { TENANT_ID, authState } = useRouteContext({ from: '/_authenticated' })
  const [files, setFiles] = useState<File[]>([])
  const [isDialogOpenInternal, setIsDialogOpenInternal] = useState(false)
  const [selectProjectDropdownValue, setSelectProjectDropdownValue] =
    useState<string>('')
  const [selectTagDropdownValue, setSelectTagDropdownValue] =
    useState<DocumentTypeTag>(DocumentTypeTag.SOP)
  const [selectCategoryDropdownValue, setSelectCategoryDropdownValue] =
    useState<CategoryTag>(defaultCategory || CategoryTag.GetStarted)
  const isAllFilesUploadedOrFailed = useUploadingStore((state) =>
    state.isAllFilesUploadedOrFailed(),
  )
  const reset = useUploadingStore((state) => state.reset)
  const [newProjectName, setNewProjectName] = useState<string>('')
  const [selectedFolder, setSelectedFolder] = useState<string>('root')
  const [isNewFolderDialogOpen, setIsNewFolderDialogOpen] = useState(false)
  const defaultProcessingResolution = useUserSettingsStore(
    (state) => state.defaultProcessingResolution,
  )

  // Fetch folders for the selected project
  const selectedProjectId =
    selectProjectDropdownValue &&
    selectProjectDropdownValue !== 'create-new-project'
      ? selectProjectDropdownValue
      : null

  const { data: folders } = useQuery({
    queryKey: queryKeys.folders.byProject(TENANT_ID!, selectedProjectId || ''),
    queryFn: async () => {
      const token = await authState!.getIdToken()
      return getFolders({
        token,
        tenantId: TENANT_ID!,
        projectId: selectedProjectId!,
      })
    },
    enabled: !!selectedProjectId && !!TENANT_ID && !!authState,
  })

  // Filter out root folder for display
  const subfolders = useMemo(
    () => folders?.filter((folder: Folder) => folder.name !== 'root') || [],
    [folders],
  )

  // Note: this is needed because the dialog does not get rerendered when user switch projects
  useEffect(() => {
    setSelectProjectDropdownValue(currentProjectId || '')
  }, [currentProjectId])

  // Reset folder selection when project changes
  useEffect(() => {
    setSelectedFolder('root')
  }, [selectProjectDropdownValue])

  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      setFiles(acceptedFiles)
    },
    [setFiles],
  )

  const handleOpenChangeInternal = useCallback(
    (open: boolean) => {
      setIsDialogOpenInternal(open)
      if (open) {
        if (isAllFilesUploadedOrFailed) {
          // Clear the uploading store state if the user opens the dialog after all previous uploads has completed or failed
          reset()
        }
        setFiles([])
      }
    },
    [isAllFilesUploadedOrFailed, reset],
  )

  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false)
  const [processingResolution, setProcessingResolution] = useState<
    'SD' | 'Original'
  >(defaultProcessingResolution)

  return (
    <Dialog
      open={isMobileVersion ? isOpen : isDialogOpenInternal} // This is for having the dialog state be controlled by the parent component on mobile
      onOpenChange={handleOpenChangeInternal}
    >
      {!isMobileVersion && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-[900px]">
        <DialogHeader className="mb-2 text-left">
          <DialogTitle>Generate Document: Upload Video</DialogTitle>
          <DialogDescription>
            {'Upload video(s) to generate a new document.'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Label htmlFor="select-project" className="gap-1">
              Select Workspace<span className="text-destructive">*</span>
            </Label>
            <Select
              defaultValue={
                projects.length > 0
                  ? currentProjectId || ''
                  : 'create-new-project'
              }
              value={selectProjectDropdownValue}
              onValueChange={setSelectProjectDropdownValue}
            >
              <SelectTrigger className="w-full" id="select-project">
                <SelectValue placeholder="Select a workspace" />
              </SelectTrigger>
              <SelectContent>
                {projects && projects.length > 0 && (
                  <>
                    <SelectGroup>
                      <SelectLabel>My Workspaces</SelectLabel>
                      {projects.map((project) => (
                        <SelectItem value={project.id} key={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectSeparator />
                  </>
                )}
                {canCreateWorkspace && (
                  <SelectItem value="create-new-project">
                    Create New Workspace...
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Label htmlFor="select-folder">Select Folder</Label>
            <Select
              value={selectedFolder}
              onValueChange={(value) => {
                if (value === 'create-new-folder') {
                  setIsNewFolderDialogOpen(true)
                } else {
                  setSelectedFolder(value)
                }
              }}
              disabled={!selectedProjectId}
            >
              <SelectTrigger className="w-full" id="select-folder">
                <SelectValue placeholder="-" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="root">-</SelectItem>
                {subfolders.length > 0 && (
                  <>
                    <SelectSeparator />
                    <SelectGroup>
                      <SelectLabel>Folders</SelectLabel>
                      {subfolders.map((folder: Folder) => (
                        <SelectItem value={folder.name} key={folder.id}>
                          {folder.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </>
                )}
                <SelectSeparator />
                <SelectItem value="create-new-folder">
                  <span className="flex items-center gap-2">
                    <FolderPlusIcon className="size-4" />
                    Create New Folder...
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Label htmlFor="select-category">
              Workspace Category<span className="text-destructive">*</span>
            </Label>
            <CategoryTagSelector
              defaultValue={CategoryTag.GetStarted}
              value={selectCategoryDropdownValue}
              onValueChange={setSelectCategoryDropdownValue}
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Label htmlFor="select-tag">
              Document Type<span className="text-destructive">*</span>
            </Label>
            <TypeTagSelector
              defaultValue={DocumentTypeTag.SOP}
              value={selectTagDropdownValue}
              onValueChange={setSelectTagDropdownValue}
            />
          </div>
        </div>
        {selectProjectDropdownValue === 'create-new-project' && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-project-name">
              New Project Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="new-project-name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder="Enter project name..."
            />
          </div>
        )}
        <Dropzone
          disabled={false}
          onDrop={handleFileDrop}
          accept={{
            'video/mp4': ['.mp4'],
            'video/avi': ['.avi'],
            'video/mov': ['.mov'],
            'video/wmv': ['.wmv'],
            'video/flv': ['.flv'],
            'video/mpeg': ['.mpeg'],
            'video/m4v': ['.m4v'],
            'video/webm': ['.webm'],
          }}
        />
        <Collapsible
          className="w-full rounded-lg border p-4 transition-all duration-300"
          open={isAdvancedSettingsOpen}
        >
          <CollapsibleTrigger
            className="flex w-full items-center justify-between text-sm font-medium"
            onClick={() => setIsAdvancedSettingsOpen(!isAdvancedSettingsOpen)}
          >
            Advanced Settings
            <ChevronDownIcon
              className={cn(
                'size-4 shrink-0 transition-all',
                isAdvancedSettingsOpen && 'rotate-180',
              )}
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Separator orientation="horizontal" className="my-4 h-full" />
            <RadioGroup
              className="flex flex-col gap-2 gap-x-4 md:flex-row"
              value={processingResolution}
              onValueChange={(value) =>
                setProcessingResolution(value as 'SD' | 'Original')
              }
            >
              <span className="flex-1 text-sm font-medium">
                Processing Resolution
              </span>
              <Label
                className={cn(
                  'align-end',
                  processingResolution === 'SD' && 'text-primary font-semibold',
                )}
              >
                <RadioGroupItem value="SD" />
                Regular (Fast)
              </Label>
              <Label
                className={cn(
                  'align-end',
                  processingResolution === 'Original' &&
                    'text-primary font-semibold',
                )}
              >
                <RadioGroupItem value="Original" />
                High (Detailed)
              </Label>
            </RadioGroup>
          </CollapsibleContent>
        </Collapsible>
        <DialogFooter>
          {!isAllFilesUploadedOrFailed && (
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
          )}
          <DialogClose asChild>
            <GenerateSOPButton
              currentProjectId={selectProjectDropdownValue}
              currentTag={selectTagDropdownValue}
              currentCategory={selectCategoryDropdownValue}
              currentFolder={
                selectedFolder !== 'root' ? selectedFolder : undefined
              }
              processingResolution={processingResolution}
              newProjectName={
                selectProjectDropdownValue === 'create-new-project'
                  ? newProjectName
                  : undefined
              }
              files={files}
              tenantId={TENANT_ID!}
              referenceDocumentDescription="Reference document"
              closeDialog={() => setIsDialogOpenInternal(false)}
              disabled={files.length === 0 || !selectProjectDropdownValue}
            />
          </DialogClose>
        </DialogFooter>
      </DialogContent>
      <NewFolderDialog
        projectId={selectedProjectId || ''}
        open={isNewFolderDialogOpen}
        onOpenChange={setIsNewFolderDialogOpen}
        onFolderCreated={(folderName) => {
          setSelectedFolder(folderName)
        }}
      />
    </Dialog>
  )
}
